import type { Archive } from "../../domain/types";
import { callLLMWithRoutes } from "../../services/llm";
import {
  resolveFeatureRoutes,
  type FeatureRoutingSettings,
} from "../../services/featureRoutes";
import { buildNarrativeMemoryPrompt, NARRATIVE_MEMORY_SYSTEM } from "./prompts";
import { mergeNarrativeFacts, retrieveNarrativeMemory } from "./logic";
import { parseNarrativeIngestPayload } from "./schemas";
import type { NarrativeFact, NarrativeMemoryPayload } from "./types";
function storedFacts(archive: Archive): NarrativeFact[] {
  return archive.narrativeMemory.facts.flatMap((value, index) => {
    try {
      const parsed = JSON.parse(value) as NarrativeFact;
      return parsed?.id ? [parsed] : [];
    } catch {
      return [
        {
          id: `NM_legacy_${index}`,
          kind: "event" as const,
          subject: "既有记忆",
          content: value,
          confidence: "confirmed" as const,
          firstSeenTurn: 0,
          lastSeenTurn: 0,
          keywords: [],
        },
      ];
    }
  });
}
function payload(archive: Archive): NarrativeMemoryPayload {
  return {
    summary: archive.narrativeMemory.summary,
    facts: storedFacts(archive),
  };
}
export function memoryContext(archive: Archive, query = "") {
  return retrieveNarrativeMemory(payload(archive), query).rendered;
}
function shouldSettle(archive: Archive, story: string) {
  const turns =
    archive.messages.filter((message) => message.role === "assistant").length +
    1;
  return (
    turns - archive.narrativeMemory.lastCompressedTurn >= 6 ||
    /任务完成|狩猎成功|捕获成功|断尾|部位破坏|晋升/.test(story)
  );
}
export async function settleNarrativeMemoryForTurn(
  archive: Archive,
  input: string,
  story: string,
  route: FeatureRoutingSettings,
): Promise<Archive> {
  if (!shouldSettle(archive, story)) return archive;
  const state = JSON.stringify({
    player: {
      identity: archive.player.identity,
      hunter: archive.player.hunter,
      wallet: archive.player.wallet,
      equipped: archive.player.equipped,
    },
    activeTask:
      archive.tasks.available.find(
        (task) => task.id === archive.tasks.activeTaskId,
      ) ?? null,
    scene: archive.scene,
    gameTime: archive.gameTime,
    monsters: archive.monsters,
  });
  try {
    const result = await callLLMWithRoutes(
      resolveFeatureRoutes("narrative-memory", route).map((candidate) => ({
        ...candidate,
        id: `narrative-memory:${candidate.id ?? candidate.model}`,
        temperature: 0.1,
        retries: 1,
      })),
      [
        { role: "system", content: NARRATIVE_MEMORY_SYSTEM },
        {
          role: "user",
          content: buildNarrativeMemoryPrompt(
            input,
            story,
            archive.narrativeMemory.summary,
            state,
          ),
        },
      ],
      { stream: false, responseFormat: "json" },
    );
    const ingest = parseNarrativeIngestPayload(result.text),
      facts = mergeNarrativeFacts(
        storedFacts(archive),
        ingest,
        archive.messages.length + 1,
      );
    const next = structuredClone(archive);
    next.narrativeMemory = {
      summary: ingest.summary,
      facts: facts.map((fact) => JSON.stringify(fact)),
      lastCompressedTurn: next.messages.length,
    };
    return next;
  } catch {
    return archive;
  }
}
